import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import React, { useState } from 'react';
import { useDocumentQuery, useMeQuery, useUpdateDocumentMutation } from '../../generated/graphql';

import { Attachments } from './components/Attachments';
import { COLORS } from '../../styles/colors';
import { StatusBadge } from '../../components/StatusBadge/StatusBadge';
import { formatTimestamp } from '../../utils';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';

const PageContainer = styled(Container)`
    padding: 2rem 0;
`;

const StyledCard = styled(Card)`
    border: none;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    margin-bottom: 2rem;
`;

const CardHeader = styled(Card.Header)`
    background: linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary});
    color: ${COLORS.white};
    border-radius: 12px 12px 0 0 !important;
    padding: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    h4 {
        font-weight: 600;
        letter-spacing: 0.5px;
        margin: 0;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    }
`;

const CardBody = styled(Card.Body)`
    padding: 2rem;
    background: ${COLORS.white};
`;

const SectionTitle = styled.h5`
    color: ${COLORS.textPrimary};
    font-weight: 600;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const InfoRow = styled(Row)`
    margin-bottom: 1.5rem;
    
    &:last-child {
        margin-bottom: 0;
    }
`;

const InfoLabel = styled(Col)`
    color: ${COLORS.textSecondary};
    font-weight: 500;
    font-size: 0.875rem;
`;

const InfoValue = styled(Col)`
    color: ${COLORS.textPrimary};
    font-weight: 500;
`;

const ActionButton = styled(Button)`
    padding: 0.5rem 1.25rem;
    border-radius: 8px;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
        transform: translateY(-1px);
    }

    &.btn-outline-light {
        border-color: rgba(255, 255, 255, 0.8);
        color: rgba(255, 255, 255, 0.9);
        background: rgba(255, 255, 255, 0.1);

        &:hover {
            background-color: rgba(255, 255, 255, 0.2);
            border-color: ${COLORS.white};
            color: ${COLORS.white};
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
    }

    &.btn-light {
        background: rgba(255, 255, 255, 0.9);
        border-color: rgba(255, 255, 255, 0.9);
        color: ${COLORS.textPrimary};

        &:hover {
            background: ${COLORS.white};
            border-color: ${COLORS.white};
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
    }
`;

const StatusContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
`;

const StyledForm = styled(Form)`
    .form-select {
        border-radius: 8px;
        padding: 0.5rem 1rem;
        font-size: 0.875rem;
        border-color: #dee2e6;
        
        &:focus {
            border-color: ${COLORS.primary};
            box-shadow: 0 0 0 0.25rem rgba(32, 178, 170, 0.25);
        }
    }
`;

const StyledSelect = styled.select`
    border-radius: 8px;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    border-color: #bdc3c7;
    background-color: ${COLORS.white};
    color: ${COLORS.textPrimary};
    
    &:focus {
        border-color: ${COLORS.primary};
        box-shadow: 0 0 0 0.25rem rgba(32, 178, 170, 0.25);
        outline: none;
    }
` as React.ComponentType<React.SelectHTMLAttributes<HTMLSelectElement>>;

export const DocumentDetailScreen: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [editStatus, setEditStatus] = useState(false);
    const [status, setStatus] = useState<number | undefined>(undefined);
    const { data: document } = useDocumentQuery({ variables: { id: Number(id) || 0 } });
    const { data: meData } = useMeQuery();
    const [updateDocument] = useUpdateDocumentMutation();

    const handleStatusChange = async () => {
        if (status === undefined || !id || !document?.document) return;
        try {
            await updateDocument({
                variables: {
                    id: Number(id),
                    input: {
                        text: document.document.text,
                        documentTypeId: document.document.documentTypeId,
                        attachmentIds: document.document.attachmentIds?.map(id => id.toString()),
                        additionalField: document.document.additionalField || '',
                    },
                },
            });
            setEditStatus(false);
        } catch (error) {
            console.error('Error updating status:', error);
        }
    };

    const isModerator = meData?.me?.moderator;

    return (
        <PageContainer>
            <StyledCard>
                <CardHeader>
                    <h4 className="mb-0">Деталі документу</h4>
                    <StatusContainer>
                        {!editStatus ? (
                            <>
                                <StatusBadge status={document?.document?.status} />
                                {isModerator && (
                                    <ActionButton 
                                        variant="outline-light" 
                                        onClick={() => setEditStatus(true)}
                                    >
                                        Змінити статус
                                    </ActionButton>
                                )}
                            </>
                        ) : (
                            <StyledForm className="d-flex gap-3">
                                <StyledSelect
                                    value={status}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setStatus(Number(e.target.value))}
                                    style={{ width: 'auto' }}
                                >
                                    <option value={0}>Розглядається</option>
                                    <option value={1}>Підтверджено</option>
                                    <option value={2}>Редагування</option>
                                    <option value={3}>Не підтверджено</option>
                                </StyledSelect>
                                <ActionButton variant="light" onClick={handleStatusChange}>
                                    Зберегти
                                </ActionButton>
                                <ActionButton variant="outline-light" onClick={() => setEditStatus(false)}>
                                    Скасувати
                                </ActionButton>
                            </StyledForm>
                        )}
                    </StatusContainer>
                </CardHeader>
                <CardBody>
                    <InfoRow>
                        <InfoLabel md={3}>Назва документу</InfoLabel>
                        <InfoValue md={9}>{document?.document?.documentType?.name}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel md={3}>Текст</InfoLabel>
                        <InfoValue md={9}>{document?.document?.text}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel md={3}>Навчальний рік</InfoLabel>
                        <InfoValue md={9}>
                            {document?.document?.educationalYear 
                                ? `${document.document.educationalYear - 1}-${document.document.educationalYear}`
                                : ''}
                        </InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel md={3}>Дата створення</InfoLabel>
                        <InfoValue md={9}>
                            {formatTimestamp(document?.document?.createdAt)}
                        </InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel md={3}>Дата оновлення</InfoLabel>
                        <InfoValue md={9}>
                            {formatTimestamp(document?.document?.updatedAt)}
                        </InfoValue>
                    </InfoRow>
                    {/* Додаткові поля (parsed and displayed nicely) */}
                    {(() => {
                        let additionalFields = {};
                        try {
                            additionalFields = JSON.parse(document?.document?.additionalField || '{}');
                        } catch (e) {
                            additionalFields = {};
                        }
                        return Object.entries(additionalFields).map(([key, value]) => (
                            <InfoRow key={key}>
                                <InfoLabel md={3}>{key}</InfoLabel>
                                <InfoValue md={9}>{value as string}</InfoValue>
                            </InfoRow>
                        ));
                    })()}
                    {/* Значення документу (parsed and displayed nicely) */}
                    {(() => {
                        let values = {};
                        try {
                            values = JSON.parse(document?.document?.documentType?.values || '{}');
                        } catch (e) {
                            values = {};
                        }
                        return Object.entries(values).map(([key, value]) => (
                            <InfoRow key={key}>
                                <InfoLabel md={3}>Кількість балів за документ</InfoLabel>
                                <InfoValue md={9}>{value as string}</InfoValue>
                            </InfoRow>
                        ));
                    })()}
                </CardBody>
            </StyledCard>

            <StyledCard>
                <CardHeader>
                    <h4 className="mb-0">Вкладення</h4>
                </CardHeader>
                <CardBody>
                    <Attachments attachments={document?.document?.attachments} />
                </CardBody>
            </StyledCard>
        </PageContainer>
    );
};
